import { useAuthStore } from "@/state/pinia";

export default [
  // ===== Default → workspace view (Witryny tab). No standalone dashboard;
  // workspaces are entered via the navbar switcher, like LessCMS. =====
  {
    path: "/",
    name: "default",
    meta: { title: "Witryny", authRequired: true },
    redirect: { name: "sites.index" },
  },

  // ===== Auth =====
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/account/login.vue"),
    meta: {
      title: "Login",
      beforeResolve(routeTo, routeFrom, next) {
        const auth = useAuthStore();
        if (auth.getToken && auth.getToken()) {
          next({ name: "default" });
        } else {
          next();
        }
      },
    },
  },
  {
    path: "/logout",
    name: "logout",
    meta: { title: "Logout", authRequired: true },
    component: () => import("@/views/account/logout.vue"),
  },
  {
    path: "/auth/callback",
    name: "sso-callback",
    component: () => import("@/views/account/sso-callback.vue"),
    meta: { title: "SSO Login", guestOnly: true },
  },

  // ===== Workspaces =====
  {
    path: "/workspaces/new",
    name: "workspaces.create",
    meta: { title: "Create workspace", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/workspaces/CreateNew.vue"),
  },
  {
    path: "/workspaces",
    name: "workspaces.select",
    meta: { title: "Select workspace", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/workspaces/Select.vue"),
  },
  {
    path: "/workspaces/:workspace_uuid/settings",
    name: "workspaces.settings",
    meta: { title: "Workspace settings", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/workspaces/SettingsView.vue"),
  },

  // ===== Projects (collapsed → witryny) =====
  // Po collapse Project→Site „projekty" nie istnieją; stare linki → /sites.
  {
    path: "/projects",
    redirect: { name: "sites.index" },
  },
  {
    path: "/project/:pathMatch(.*)*",
    redirect: { name: "sites.index" },
  },

  // ===== Sites (LessSEO domain) =====
  {
    path: "/sites",
    name: "sites.index",
    meta: { title: "Witryny", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/sites/SitesList.vue"),
  },
  {
    path: "/sites/:uuid",
    name: "sites.show",
    meta: { title: "Strona", authRequired: true },
    component: () => import("@/views/sites/SiteOverview.vue"),
  },
  {
    path: "/sites/:uuid/timeline",
    name: "sites.timeline",
    meta: { title: "Timeline", authRequired: true },
    component: () => import("@/views/timeline/SiteTimeline.vue"),
  },
  {
    path: "/sites/:uuid/analytics",
    name: "sites.analytics",
    meta: { title: "Ruch", authRequired: true },
    component: () => import("@/views/sites/AnalyticsDetail.vue"),
  },
  {
    path: "/sites/:uuid/alerts",
    name: "sites.alerts",
    meta: { title: "Alerty", authRequired: true },
    component: () => import("@/views/sites/AlertsView.vue"),
  },
  {
    path: "/sites/:uuid/competitors",
    name: "sites.competitors",
    meta: { title: "Konkurencja", authRequired: true },
    component: () => import("@/views/sites/CompetitorsView.vue"),
  },
  {
    path: "/sites/:uuid/backlinks",
    name: "sites.backlinks",
    meta: { title: "Backlinki", authRequired: true },
    component: () => import("@/views/sites/BacklinksView.vue"),
  },
  {
    path: "/sites/:uuid/performance",
    name: "sites.performance",
    meta: { title: "Wydajność", authRequired: true },
    component: () => import("@/views/sites/PerformanceView.vue"),
  },
  {
    path: "/sites/:uuid/recommendations",
    name: "sites.recommendations",
    meta: { title: "Rekomendacje", authRequired: true },
    component: () => import("@/views/sites/RecommendationsView.vue"),
  },
  {
    path: "/sites/:uuid/ads",
    name: "sites.ads",
    meta: { title: "Google Ads", authRequired: true },
    component: () => import("@/views/sites/AdsView.vue"),
  },
  {
    path: "/sites/:uuid/locations",
    name: "sites.locations",
    meta: { title: "Lokalizacje", authRequired: true },
    component: () => import("@/views/sites/LocationsView.vue"),
  },
  {
    path: "/sites/:uuid/sources",
    name: "sites.sources",
    meta: { title: "Źródła odwiedzin", authRequired: true },
    component: () => import("@/views/sites/SourcesView.vue"),
  },
  {
    path: "/sites/:uuid/journey",
    name: "sites.journey",
    meta: { title: "Podróż użytkownika", authRequired: true },
    component: () => import("@/views/sites/JourneyView.vue"),
  },
  {
    path: "/sites/:uuid/goals",
    name: "sites.goals",
    meta: { title: "Cele", authRequired: true },
    component: () => import("@/views/sites/GoalsView.vue"),
  },
  {
    path: "/sites/:uuid/queries",
    name: "sites.queries",
    meta: { title: "Frazy organiczne", authRequired: true },
    component: () => import("@/views/sites/GscBreakdown.vue"),
    props: { mode: "queries" },
  },
  {
    path: "/sites/:uuid/landing-pages",
    name: "sites.landing-pages",
    meta: { title: "Strony organiczne", authRequired: true },
    component: () => import("@/views/sites/GscBreakdown.vue"),
    props: { mode: "pages" },
  },
  {
    path: "/sites/:uuid/keywords",
    name: "sites.keywords",
    meta: { title: "Słowa kluczowe", authRequired: true },
    component: () => import("@/views/keywords/KeywordsList.vue"),
  },
  {
    path: "/sites/:uuid/keywords/:id",
    name: "sites.keyword.detail",
    meta: { title: "Fraza", authRequired: true },
    component: () => import("@/views/keywords/KeywordDetail.vue"),
  },
  {
    path: "/sites/:uuid/audits",
    name: "sites.audits",
    meta: { title: "Audyty SEO", authRequired: true },
    component: () => import("@/views/audits/AuditsList.vue"),
  },
  {
    path: "/sites/:uuid/audits/:id",
    name: "sites.audit.detail",
    meta: { title: "Audyt", authRequired: true },
    component: () => import("@/views/audits/AuditDetail.vue"),
  },
  {
    path: "/sites/:uuid/marketing",
    name: "sites.marketing",
    meta: { title: "Planowanie postów", authRequired: true },
    component: () => import("@/views/marketing/PlannerIndex.vue"),
  },
  {
    path: "/sites/:uuid/marketing/:targetId",
    name: "sites.marketing.target",
    meta: { title: "Planowanie postów", authRequired: true },
    component: () => import("@/views/marketing/PlannerView.vue"),
  },
  {
    path: "/sites/:uuid/reports",
    name: "sites.reports",
    meta: { title: "Raporty", authRequired: true },
    component: () => import("@/views/reports/ReportsList.vue"),
  },
  {
    path: "/sites/:uuid/settings",
    name: "sites.settings",
    meta: { title: "Ustawienia witryny", authRequired: true },
    component: () => import("@/views/sites/SiteSettings.vue"),
  },

  // ===== Settings =====
  {
    path: "/settings/integrations",
    name: "settings.integrations",
    meta: { title: "Integracje", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/settings/Integrations.vue"),
  },

  // ===== Billing =====
  {
    path: "/billing",
    name: "billing.plans",
    meta: { title: "Subskrypcja", authRequired: true, workspaceLevel: true },
    // Ten sam widok co w LessCMS i LessCommerce: zakładki workspace'u, zużycie
    // limitów, a plany/płatności/faktury w panelu rozliczeń SSO.
    component: () => import("@/views/subscriptions/subscription-cart.vue"),
  },

  // ===== Users =====
  {
    path: "/users",
    name: "users.list",
    meta: { title: "Użytkownicy", authRequired: true, workspaceLevel: true },
    component: () => import("@/views/users/users-list.vue"),
  },

  // ===== Fallback =====
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "default" },
  },
];
